/**
 * AdvancedOptionsCard — options facturation étendues (Sprint 89).
 *
 * Regroupe dans un panneau dépliable les fonctionnalités parité Axonaut :
 *  - Type de document (facture / proforma / acompte / situation)
 *  - Remise globale (% ou €)
 *  - Frais annexes (port, expédition)
 *  - Acompte encaissé
 *  - Retenue de garantie (BTP)
 *  - Facture de situation (avancement + réf devis + déjà facturé)
 *  - Moyen de paiement + IBAN + BIC
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronRight, Settings2 } from "lucide-react";

const DOC_KINDS = [
  { value: "invoice",         label: "Facture standard",     desc: "Émission classique avec numéro légal" },
  { value: "proforma",        label: "Proforma",             desc: "Offre commerciale, sans valeur comptable" },
  { value: "acompte",         label: "Facture d'acompte",    desc: "Acompte partiel à déduire de la facture finale" },
  { value: "situation",       label: "Facture de situation", desc: "Facturation par avancement (BTP / projets longs)" },
  { value: "purchase_order",  label: "Bon de commande",      desc: "Confirmation de commande signée par le client" },
  { value: "delivery_note",   label: "Bon de livraison",     desc: "Preuve de livraison, à signer par le client" },
];

const PAYMENT_METHODS = [
  { value: "virement",    label: "Virement bancaire" },
  { value: "cheque",      label: "Chèque" },
  { value: "cb",          label: "Carte bancaire" },
  { value: "prelevement", label: "Prélèvement SEPA" },
  { value: "especes",     label: "Espèces" },
  { value: "autre",       label: "Autre" },
];

export default function AdvancedOptionsCard({ value, onChange, readOnly }) {
  const [open, setOpen] = useState(false);
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const isSituation = v.doc_kind === "situation";

  return (
    <Card className="border-slate-200 bg-white rounded-xl overflow-hidden" data-testid="advanced-options-card">
      <button type="button"
              onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition"
              data-testid="advanced-options-toggle">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800"
                 style={{ fontFamily: "Manrope, sans-serif" }}>
            Options avancées
          </span>
          <span className="text-xs text-slate-400 hidden sm:inline">
            (remise, acompte, retenue, moyens de paiement…)
          </span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" />
              : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-2 space-y-5 border-t border-slate-100">
          {/* Type de document */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
              Type de document
            </Label>
            <Select value={v.doc_kind || "invoice"} onValueChange={(x) => set("doc_kind", x)}
                    disabled={readOnly}>
              <SelectTrigger className="bg-white border-slate-200" data-testid="doc-kind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{k.label}</span>
                      <span className="text-[10px] text-slate-500">{k.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Remise globale + Frais annexes — 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                Remise globale
              </Label>
              <p className="text-[10px] text-slate-500 -mt-1">
                En pourcentage OU en montant (€ prime si les 2 sont saisis)
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">%</Label>
                  <Input type="number" min="0" max="100" step="0.01"
                          value={v.global_discount_percent ?? ""} disabled={readOnly}
                          onChange={(e) => set("global_discount_percent",
                                                  e.target.value === "" ? null : Number(e.target.value))}
                          className="font-mono bg-white" data-testid="global-discount-pct" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Montant €</Label>
                  <Input type="number" min="0" step="0.01"
                          value={v.global_discount_amount ?? ""} disabled={readOnly}
                          onChange={(e) => set("global_discount_amount",
                                                  e.target.value === "" ? null : Number(e.target.value))}
                          className="font-mono bg-white" data-testid="global-discount-amount" />
                </div>
              </div>
              <Input placeholder="Libellé (ex: Remise fidélité)" maxLength={80}
                      value={v.global_discount_label || ""} disabled={readOnly}
                      onChange={(e) => set("global_discount_label", e.target.value)}
                      className="text-xs bg-white" />
            </div>

            <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                Frais annexes (port, expédition…)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Montant HT</Label>
                  <Input type="number" min="0" step="0.01"
                          value={v.shipping_amount_ht ?? ""} disabled={readOnly}
                          onChange={(e) => set("shipping_amount_ht",
                                                  e.target.value === "" ? null : Number(e.target.value))}
                          className="font-mono bg-white" data-testid="shipping-ht" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">TVA %</Label>
                  <Input type="number" min="0" max="100" step="0.1"
                          value={v.shipping_vat_rate ?? 20} disabled={readOnly}
                          onChange={(e) => set("shipping_vat_rate", Number(e.target.value))}
                          className="font-mono bg-white" />
                </div>
              </div>
              <Input placeholder="Libellé (défaut: Frais de port)" maxLength={80}
                      value={v.shipping_label || ""} disabled={readOnly}
                      onChange={(e) => set("shipping_label", e.target.value)}
                      className="text-xs bg-white" />
            </div>
          </div>

          {/* Acompte + Retenue garantie */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                Acompte déjà encaissé (TTC)
              </Label>
              <p className="text-[10px] text-slate-500 -mt-1">Déduit du solde à payer</p>
              <Input type="number" min="0" step="0.01"
                      value={v.deposit_amount_ttc ?? ""} disabled={readOnly}
                      onChange={(e) => set("deposit_amount_ttc",
                                              e.target.value === "" ? null : Number(e.target.value))}
                      className="font-mono bg-white" data-testid="deposit-ttc" />
              <Input placeholder="Libellé (ex: Acompte 30% du 15/03)" maxLength={80}
                      value={v.deposit_label || ""} disabled={readOnly}
                      onChange={(e) => set("deposit_label", e.target.value)}
                      className="text-xs bg-white" />
            </div>

            <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                Retenue de garantie (BTP)
              </Label>
              <p className="text-[10px] text-slate-500 -mt-1">
                % du TTC retenu jusqu'à levée des réserves (typiquement 5%)
              </p>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" step="0.1"
                        value={v.retenue_garantie_percent ?? ""} disabled={readOnly}
                        onChange={(e) => set("retenue_garantie_percent",
                                                e.target.value === "" ? null : Number(e.target.value))}
                        className="font-mono bg-white" data-testid="retenue-pct" />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>
          </div>

          {/* Situation BTP */}
          {isSituation && (
            <div className="space-y-2 border border-emerald-200 rounded-lg p-3 bg-emerald-50">
              <Label className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
                Facture de situation — avancement chantier
              </Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Avancement %</Label>
                  <Input type="number" min="0" max="100" step="0.1"
                          value={v.situation_progress_percent ?? ""} disabled={readOnly}
                          onChange={(e) => set("situation_progress_percent",
                                                  e.target.value === "" ? null : Number(e.target.value))}
                          className="font-mono bg-white" data-testid="situation-progress" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Devis référence</Label>
                  <Input maxLength={60} value={v.situation_quote_reference || ""}
                          disabled={readOnly}
                          onChange={(e) => set("situation_quote_reference", e.target.value)}
                          placeholder="D-2025-00042" className="bg-white" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">HT déjà facturé (cumul)</Label>
                  <Input type="number" min="0" step="0.01"
                          value={v.situation_previous_ht ?? ""} disabled={readOnly}
                          onChange={(e) => set("situation_previous_ht",
                                                  e.target.value === "" ? null : Number(e.target.value))}
                          className="font-mono bg-white" />
                </div>
              </div>
            </div>
          )}

          {/* Moyens de paiement + IBAN/BIC */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
              Coordonnées de paiement (affichées sur le PDF)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500">Mode</Label>
                <Select value={v.payment_method || ""}
                        onValueChange={(x) => set("payment_method", x || null)}
                        disabled={readOnly}>
                  <SelectTrigger className="bg-white border-slate-200"
                                  data-testid="payment-method-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <Label className="text-[10px] text-slate-500">IBAN</Label>
                <Input value={v.payment_iban || ""} disabled={readOnly}
                        onChange={(e) => set("payment_iban", e.target.value)}
                        placeholder="FR76 3000 6000 0112 3456 7890 189"
                        className="font-mono text-xs bg-white"
                        data-testid="payment-iban" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">BIC (facultatif)</Label>
                <Input value={v.payment_bic || ""} disabled={readOnly}
                        onChange={(e) => set("payment_bic", e.target.value)}
                        placeholder="AGRIFRPP"
                        className="font-mono text-xs bg-white" />
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
